import { useConnection } from "@solana/wallet-adapter-react";
import type { Connection, PublicKey } from "@solana/web3.js";
import { aprToApy, convert, SYSTEM_PROGRAM_ID } from "@stepfinance/common";
import type { LendingCollateralPositionMap, LeveragedYieldPositionMap } from "@stepfinance/lending";
import type { NormalizedTokenAccount } from "@stepfinance/tokens";
import BigNumber from "bignumber.js";
import BN from "bn.js";
import { useEffect, useState } from "react";
import useSWRImmutable from "swr/immutable";

import { WAD } from "../../constants";
import { getMultipleAccounts } from "../../contexts/accounts";
import { useNetworkConfig } from "../../contexts/app/config";
import { useEnrichedWallet } from "../../contexts/enrichedWallet";
import type { MarketsContextState } from "../../contexts/market";
import { useMarkets } from "../../contexts/market";
import { LENDING_RESERVE_LAYOUT, MINT_INFO_LAYOUT } from "../../models/token-lending/tulip/reserve";
import type { TulipReserve } from "../../models/token-lending/tulip/reserves";
import { LENDING_OBLIGATION_LAYOUT } from "../../models/tulip";
import type { FarmObligationMap, FarmsWithPda, TulipLeveragedFarms } from "../../models/tulip/leverageVaults";
import { TULIP_FARM_PROGRAM_ID } from "../../types/tulips";
import { calculateTulipBorrowApr } from "../../utils/lending/tulip";
import { TokenAmount } from "../../utils/raydium/safe-math";
import { STEP_API_ENDPOINT, swrFetcher } from "../../utils/swr";
import { findUserFarmAddress, findUserFarmObligationAddress, getVaultInfo } from "../../utils/tulip/leveraged";
import { useConnectedWallet } from "../wallet/useConnectedWallet";

const getUserCollateralPositions = async (
  connection: Connection,
  userAccounts: NormalizedTokenAccount[],
  tulipReserves: TulipReserve[],
): Promise<LendingCollateralPositionMap> => {
  const userUtilizedReserves = tulipReserves.filter((reserve) =>
    userAccounts.find((x) => x.info.mint === reserve.collateralTokenMint),
  );

  const accountPublicKeys: string[] = userUtilizedReserves.map((reserve) => reserve.account);
  const collateralMints: string[] = userUtilizedReserves.map((reserve) => reserve.collateralTokenMint);
  const accounts = await getMultipleAccounts(connection, [...accountPublicKeys, ...collateralMints], "confirmed");

  if (!accounts.array.length) {
    return {};
  }

  const collateralPositionMap: LendingCollateralPositionMap = {};
  const collateralMintAccounts = accounts.array.slice(userUtilizedReserves.length, userUtilizedReserves.length * 2);

  for (let i = 0; i < userUtilizedReserves.length; i++) {
    const reserve = userUtilizedReserves[i];
    const reserveAccount = accounts.array[i];
    if (reserveAccount?.data) {
      const decoded = LENDING_RESERVE_LAYOUT.decode(reserveAccount.data);
      const collateralMintAccount = collateralMintAccounts[i];
      if (reserveAccount && reserve && collateralMintAccount) {
        const { borrowedAmountWads, platformAmountWads, availableAmount, mintDecimals } = decoded.liquidity;

        const borrowedAmount = new TokenAmount(borrowedAmountWads.div(WAD).toString(), mintDecimals);
        const platformAmount = new TokenAmount(platformAmountWads.div(WAD).toString(), mintDecimals);

        const availableAmountWei = new TokenAmount(availableAmount.toString(), mintDecimals);
        const account = userAccounts.find((x) => x.info.mint === reserve.collateralTokenMint);

        if (account) {
          const totalSupply = availableAmountWei.wei.plus(borrowedAmount.wei).minus(platformAmount.wei);

          const decodedCollateralAccount = MINT_INFO_LAYOUT.decode(collateralMintAccount.data);

          const collateralTokenBalance = new BigNumber(
            convert(new BN(account.tokenAmount.amount), decodedCollateralAccount.decimals),
          );

          const currentUtilization = new BigNumber(borrowedAmount.wei).dividedBy(
            new BigNumber(borrowedAmount.wei).plus(availableAmountWei.wei),
          );

          const borrowApr = calculateTulipBorrowApr(Number(currentUtilization.multipliedBy(100)), reserve);
          const depositApr = Number(currentUtilization.multipliedBy(borrowApr));

          const uiAmount = decodedCollateralAccount.supply.div(
            new BN(10).pow(new BN(decodedCollateralAccount.decimals)),
          );

          const deposited = collateralTokenBalance
            .multipliedBy(totalSupply)
            .dividedBy(uiAmount.toString())
            .dividedBy(new BigNumber(10).exponentiatedBy(mintDecimals));

          if (deposited.gt(0)) {
            const mintAddress = reserve.mintAddress;
            collateralPositionMap[mintAddress] = {
              deposit: {
                amount: deposited.toNumber(),
                apy: aprToApy(depositApr) / 100,
              },
              borrow: {
                amount: 0,
                apy: null,
              },
              display: true,
            };
          }
        }
      }
    }
  }
  return collateralPositionMap;
};

const getLeveragedPositions = async (
  connection: Connection,
  pubkey: PublicKey,
  tulipLeveragedFarms: TulipLeveragedFarms[],
  priceInUsd: MarketsContextState["priceInUSD"],
  tulipReserves: TulipReserve[],
) => {
  const yieldFarms: LeveragedYieldPositionMap = {};

  const userFarmsWithPda: FarmsWithPda[] = (
    await Promise.all(
      tulipLeveragedFarms.map(async (farm) => {
        const { farm_key } = farm;
        const farmAddress = await findUserFarmAddress(
          pubkey,
          TULIP_FARM_PROGRAM_ID,
          new BigNumber(0),
          new BigNumber(farm_key),
        );
        if (farmAddress) {
          return {
            ...farm,
            farmAddress,
          };
        }
      }),
    )
  ).filter((farm): farm is FarmsWithPda => farm !== undefined);

  const userFarmObligations = Promise.all(
    userFarmsWithPda.map(async (farm) => {
      const accounts: PublicKey[] = [];
      for (let i = 0; i <= 2; i++) {
        const userObligationAcct1 = await findUserFarmObligationAddress(
          pubkey,
          farm.farmAddress[0],
          TULIP_FARM_PROGRAM_ID,
          new BigNumber(i),
        );
        if (userObligationAcct1 && userObligationAcct1[0]) {
          accounts.push(userObligationAcct1[0]);
        }
      }
      return { obligationAccounts: accounts, ...farm };
    }),
  );
  const userFarmObligationsMap: FarmObligationMap = (await userFarmObligations).reduce((map, farm) => {
    farm.obligationAccounts.forEach((obligation) => {
      map[obligation.toBase58()] = farm;
    });
    return map;
  }, {});

  const farmObligationAddresses = Object.keys(userFarmObligationsMap);
  const { keys: obligationKeys, array: obligationAccounts } = await getMultipleAccounts(
    connection,
    farmObligationAddresses,
    "confirmed",
  );

  const liquidationThreshold = 0.85;
  for (let i = 0; i < obligationAccounts.length; i++) {
    const obligation = obligationAccounts[i];
    const key = obligationKeys[i];
    const obligationData = LENDING_OBLIGATION_LAYOUT.decode(obligation.data);
    if (obligationData.depositsLen === 0 && obligationData.borrowsLen === 0) {
      continue;
    }
    const farmObligation = userFarmObligationsMap[key];
    const isOrca = farmObligation.raydium_amm_open_orders === SYSTEM_PROGRAM_ID.toBase58();
    const poolVersion = isOrca ? 1 : 0;
    const vaultInfo = await getVaultInfo(
      connection,
      farmObligation,
      priceInUsd,
      obligationData,
      poolVersion,
      tulipReserves,
    );

    if (vaultInfo === undefined) {
      continue;
    }

    const debtRatio = vaultInfo.debtValue / vaultInfo.virtualValue.toNumber();

    yieldFarms[`${farmObligation.raydium_lp_mint_address}-${i}`] = {
      mintA: vaultInfo.mintAData.address,
      mintB: vaultInfo.mintBData.address,
      debtTokenAmountA: vaultInfo.mintAData.borrowedAmount,
      debtTokenAmountB: vaultInfo.mintBData.borrowedAmount,
      equityUSD: vaultInfo.equityValue.toNumber(),
      debtTotalValueUSD: vaultInfo.debtValue,
      positionAmountA: vaultInfo.positionAmountA,
      positionAmountB: vaultInfo.positionAmountB,
      killBuffer: liquidationThreshold - debtRatio,
      positionValueUSD: vaultInfo.virtualValue.toNumber(),
    };
  }

  return yieldFarms;
};

export const useTulipLending = (userTokenAccounts) => {
  const { connection } = useConnection();
  const { network } = useNetworkConfig();
  const wallet = useConnectedWallet();
  // FIXME: this will be fixed in SF-1531 https://step-finance.atlassian.net/browse/SF-1531
  // const { userTokenAccounts } = useAccountsContext();
  const { connected } = useEnrichedWallet();
  const { marketsInitialised, refreshing, priceInUSD } = useMarkets();
  const { data: tulipReserves } = useSWRImmutable<TulipReserve[]>(
    `${STEP_API_ENDPOINT}/v1/integration/tulip/reserves`,
    swrFetcher,
  );
  const { data: tulipLeveragedFarms } = useSWRImmutable<TulipLeveragedFarms[]>(
    `${STEP_API_ENDPOINT}/v1/integration/tulip/leverage`,
    swrFetcher,
  );

  const [collateralPositionMap, setCollateralPositionMap] = useState<LendingCollateralPositionMap>({});
  const [leveragedYieldMap, setLeveragedYieldMap] = useState<LeveragedYieldPositionMap>({});

  useEffect(() => {
    setCollateralPositionMap({});
    setLeveragedYieldMap({});
  }, [connected]);

  useEffect(() => {
    const hasUserAccounts = userTokenAccounts.length > 0;
    const isMainnet = network === "mainnet-beta";
    const marketsRefreshFinished = !refreshing;
    if (
      connected &&
      wallet &&
      hasUserAccounts &&
      marketsRefreshFinished &&
      isMainnet &&
      marketsInitialised &&
      tulipLeveragedFarms &&
      tulipReserves
    ) {
      void getUserCollateralPositions(connection, userTokenAccounts, tulipReserves).then(setCollateralPositionMap);
      void getLeveragedPositions(connection, wallet.publicKey, tulipLeveragedFarms, priceInUSD, tulipReserves).then(
        setLeveragedYieldMap,
      );
    }
  }, [
    connected,
    connection,
    network,
    wallet,
    refreshing,
    tulipLeveragedFarms,
    userTokenAccounts,
    priceInUSD,
    marketsInitialised,
    tulipReserves,
  ]);

  return { collateral: collateralPositionMap, yieldFarms: leveragedYieldMap };
};
